/**
 * 브라우저 탭 제목 = "현재 메뉴 - 앱 이름" (구포 #75 — 새 탭으로 여러 화면을 열었을 때 구분).
 *
 * 제목은 두 조각으로 관리한다: 앱 이름(브랜딩 — 부팅·테넌트 브랜딩 로드·브랜딩 설정 저장 때 갱신)과
 * 현재 메뉴 이름(라우트 이동·메뉴 트리 로드 때 갱신). 어느 쪽이 바뀌어도 둘을 합쳐 다시 쓴다.
 * 채팅 안 읽음 접두어 "(3) " 는 보존한다(ChatRealtimeBootstrap 가 접두어만 갈아 끼움).
 */
import { watch } from 'vue';
import type { Pinia } from 'pinia';
import type { Router } from 'vue-router';

import type { MenuTreeNode } from '../api/menus';
import { stripTenantPrefix } from '../router/login-route';
import { useMenuStore } from '../stores/menu';

const UNREAD_PREFIX_RE = /^\(\d+\)\s*/;

let brandTitle = '';
let pageLabel: string | null = null;

export function buildDocumentTitle(label: string | null, brand: string, current: string): string {
  const prefix = UNREAD_PREFIX_RE.exec(current)?.[0] ?? '';
  const base = label && brand ? `${label} - ${brand}` : label || brand;
  return `${prefix}${base}`;
}

function render(): void {
  if (typeof document === 'undefined') return;
  const next = buildDocumentTitle(pageLabel, brandTitle, document.title);
  if (next !== document.title) document.title = next;
}

export function setDocumentBrandTitle(title: string): void {
  brandTitle = title;
  render();
}

export function setDocumentPageLabel(label: string | null): void {
  pageLabel = label;
  render();
}

/** 메뉴 트리에서 path 가 같은 leaf 의 이름 — 없으면 null */
export function findMenuLabel(tree: readonly MenuTreeNode[], path: string): string | null {
  for (const n of tree) {
    if (n.path === path) return n.name;
    const sub = findMenuLabel(n.children, path);
    if (sub) return sub;
  }
  return null;
}

/**
 * 라우트·메뉴 트리 변화에 맞춰 현재 메뉴 이름을 탭 제목에 반영한다.
 * 메뉴에 없는 화면(상세·POP 등)은 라우트 meta.title, 그것도 없으면 앱 이름만.
 */
export function installDocumentTitleSync(router: Router, pinia: Pinia): void {
  const menuStore = useMenuStore(pinia);
  watch(
    [() => router.currentRoute.value, () => menuStore.tree],
    ([route, tree]) => {
      const fromMenu = findMenuLabel(tree, stripTenantPrefix(route.path));
      const metaTitle = typeof route.meta.title === 'string' ? route.meta.title : null;
      setDocumentPageLabel(fromMenu ?? metaTitle);
    },
    { immediate: true },
  );
}
